// alert.service.ts
import { Injectable } from '@angular/core';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';

type ToastOptions = Omit<SweetAlertOptions, 'toast' | 'position'>;
type ModalOptions = Omit<SweetAlertOptions, 'input' | 'inputValidator'>;

@Injectable({ providedIn: 'root' })
export class AlertService {
  //#region Toast Configuration
  private readonly MAX_TOAST_MS = 5000;

  private readonly toastBaseConfig: SweetAlertOptions = {
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener('mouseenter', Swal.stopTimer);
      toast.addEventListener('mouseleave', Swal.resumeTimer);
    },
  };

  /** Build a toast with a guaranteed timer ≤ MAX_TOAST_MS */
  private fireToast(options: ToastOptions): Promise<SweetAlertResult> {
    const desired =
      options.timer ?? this.toastBaseConfig.timer ?? this.MAX_TOAST_MS;
    const safeTimer = Math.min(desired, this.MAX_TOAST_MS);

    return Swal.fire({
      ...this.toastBaseConfig,
      ...options,
      timer: safeTimer,
    } as SweetAlertOptions);
  }

  // Success toast
  successToast(title: string, text?: string): Promise<SweetAlertResult> {
    return this.fireToast({ icon: 'success', title, text });
  }

  // Error toast (longest allowed)
  errorToast(title: string, text?: string): Promise<SweetAlertResult> {
    return this.fireToast({ icon: 'error', title, text, timer: 2000 });
  }

  // Warning toast (capped at 5000)
  warningToast(title: string, text?: string): Promise<SweetAlertResult> {
    return this.fireToast({ icon: 'warning', title, text, timer: 2000 });
  }

  // Info toast
  infoToast(title: string, text?: string): Promise<SweetAlertResult> {
    return this.fireToast({ icon: 'info', title, text });
  }

  // Custom toast (any provided timer will be clamped to ≤ 5000)
  customToast(options: ToastOptions): Promise<SweetAlertResult> {
    return this.fireToast(options);
  }
  //#endregion

  //#region Modal Configuration
  private readonly modalBaseConfig: SweetAlertOptions = {
    buttonsStyling: false,
    heightAuto: false,
    allowOutsideClick: false,
  };

  // Basic modal alert
  showModal(options: ModalOptions): Promise<SweetAlertResult> {
    return Swal.fire({
      ...this.modalBaseConfig,
      ...options,
    });
  }

  // Success modal
  successModal(title: string, text?: string): Promise<SweetAlertResult> {
    return this.showModal({
      icon: 'success',
      title,
      text,
      confirmButtonText: 'OK',
    });
  }

  // Error modal
  errorModal(title: string, text?: string): Promise<SweetAlertResult> {
    return this.showModal({
      icon: 'error',
      title,
      text,
      confirmButtonText: 'OK',
    });
  }

  // Info modal
  infoModal(title: string, text?: string): Promise<SweetAlertResult> {
    return this.showModal({
      icon: 'info',
      title,
      text,
      confirmButtonText: 'OK',
    });
  }

  // Confirm dialog
  confirmModal(options: ModalOptions): Promise<SweetAlertResult> {
    return Swal.fire({
      ...this.modalBaseConfig,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      ...options,
    });
  }

  insufficientFundsModal(): Promise<SweetAlertResult> {
    return this.confirmModal({
      icon: 'warning',
      title: 'Insufficient funds',
      text: 'Kindly fund your wallet to proceed',
      confirmButtonText: 'Fund Wallet',
    });
  }

  // Blocking, non-dismissible except via the Refresh button: shown when we
  // can't tell whether a user is verified (e.g. the revalidation-status API
  // itself failed), as opposed to the user genuinely being unverified.
  // Shared by every call site that checks revalidation status, so the
  // message/behavior can't drift between them the way it did when only one
  // of the two call sites got this fix. `onRefresh` re-runs that call site's
  // own revalidation check in place (no page reload).
  verificationUnavailableModal(onRefresh: () => void): void {
    this.showModal({
      icon: 'error',
      title: "We couldn't verify your account",
      text: 'Click refresh to try again.',
      confirmButtonText: 'Refresh',
      allowEscapeKey: false,
      customClass: { confirmButton: 'prjct-btn' },
    }).then((result) => {
      if (result.isConfirmed) {
        onRefresh();
      }
    });
  }

  // Loading modal (no timer, blocks until closed)
  showLoadingModal(title: string, text?: string): void {
    Swal.fire({
      ...this.modalBaseConfig,
      title,
      text,
      allowEscapeKey: false,
      allowOutsideClick: false,
      didOpen: () => Swal.showLoading(),
    });
  }
  //#endregion

  // Close all active alerts
  closeAlerts(): void {
    Swal.close();
  }
}
